import { Chip, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { FilePlus01, Pencil02, RefreshCCW04 } from 'untitledui-js-base';
import { GenerateDocumentButton } from '../common/GenerateDocumentButton';
import { ACCIDENT_FILES, TenantType } from '../types/accident';
import { GenerableDocument } from '../types/rental';
import { ConfirmRegenerateDocumentDialog } from './ConfirmRegenerateDocumentDialog';

interface DocumentActionsProps {
  document: GenerableDocument;
  template: ACCIDENT_FILES;
  isGeneratingDocument: boolean;
  handleGenerateDocument: (template: string, regenerate?: boolean, tenantType?: TenantType) => void;
  setSelectedDocumentName?: (template: ACCIDENT_FILES, tenant?: TenantType) => void;
  chooseTenant?: boolean;
}

export const DocumentActions = ({
  document,
  template,
  isGeneratingDocument,
  handleGenerateDocument,
  setSelectedDocumentName,
  chooseTenant
}: DocumentActionsProps) => {
  const [tenantType, setTenantType] = useState(TenantType.OWNER);
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);

  const isGenerated = Boolean(document?.path);
  const requestSignature = document?.requestSignature === true;
  const allowRegenerate = document?.allowRegenerate === true;
  const allowPreview = document?.allowPreview === true;

  const isSigned =
    (document as any)?.signed === true ||
    (document as any)?.signature?.status === 'SIGNED' ||
    Boolean((document as any)?.signedAt) ||
    /(^|[_-])signed(\.|$)/i.test(`${document?.filename || ''}${document?.path || ''}`);
  const awaitingSignature = requestSignature && !isSigned;

  const showSignAction = awaitingSignature;
  const showGenerate = !isGenerated && (!requestSignature || allowPreview);
  const showRegenerate = isGenerated && allowRegenerate;
  const showDownload = isGenerated;

  const handleGenerateDocumentClick = (regenerate?: boolean) => {
    handleGenerateDocument(template, regenerate, chooseTenant ? tenantType : undefined);
  };

  const handleRegenerateClick = () => {
    if (isSigned) {
      setConfirmRegenOpen(true);
      return;
    }
    handleGenerateDocumentClick(true);
  };

  const handleConfirmRegen = () => {
    setConfirmRegenOpen(false);
    handleGenerateDocumentClick(true);
  };

  return (
    <>
      <Stack direction={{ md: 'row' }} spacing={5} alignItems={{ md: 'center' }}>
        {chooseTenant && (
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="tenant-label"
              name="tenant"
              value={tenantType}
              onChange={(_, value) => setTenantType(value as TenantType)}
            >
              <FormControlLabel
                value={TenantType.OWNER}
                control={<Radio size="small" sx={{ px: '5px', py: '2px' }} />}
                label="Majitel"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
              />
              <FormControlLabel
                value={TenantType.CONTACT_PERSON}
                control={<Radio size="small" sx={{ px: '5px', py: '2px' }} />}
                label="Kontaktní osoba"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
              />
            </RadioGroup>
          </FormControl>
        )}

        <Stack direction="row" spacing={1} mt={{ xs: 1, md: 0 }}>
          {showSignAction && (
            <Tooltip title={awaitingSignature ? 'Podepsat dokument' : 'Otevřít náhled'}>
              <IconButton
                color="primary"
                size="small"
                onClick={() => setSelectedDocumentName?.(template, chooseTenant ? tenantType : undefined)}
                disabled={isGeneratingDocument}
              >
                <Pencil02 size="20px" />
              </IconButton>
            </Tooltip>
          )}

          {showGenerate && (
            <GenerateDocumentButton
              generateIcon={<FilePlus01 width={16} height={16} />}
              onClick={() => handleGenerateDocumentClick()}
              loading={isGeneratingDocument}
            />
          )}

          {showDownload && (
            <Stack direction="row" gap={2} alignItems="center">
              <Chip
                variant="outlined"
                label={isSigned ? 'Podepsáno' : awaitingSignature ? 'Připraveno k podpisu' : 'Vygenerováno'}
                color={awaitingSignature ? 'warning' : 'success'}
              />
              {showRegenerate && (
                <Tooltip title={isSigned ? 'Přegenerovat (znovu podepsat)' : 'Přegenerovat dokument'}>
                  <IconButton color="info" size="small" onClick={handleRegenerateClick} disabled={isGeneratingDocument}>
                    <RefreshCCW04 width={20} height={20} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      <ConfirmRegenerateDocumentDialog
        setConfirmRegenOpen={(value) => setConfirmRegenOpen(value)}
        confirmRegenOpen={confirmRegenOpen}
        handleConfirmRegen={handleConfirmRegen}
        isGeneratingDocument={isGeneratingDocument}
      />
    </>
  );
};
